import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Menu, User, LogOut, PanelLeftClose, PanelLeftOpen, Home, ChevronRight } from 'lucide-react';
import Sidebar from './Sidebar';
import SessionGuard from './SessionGuard';
import BASE_URL from '../services/api';

const PAGE_TITLES = {
  '/integrated-dashboard': 'Integrated Dashboard',
  '/district-ranking': 'District Ranking',
  '/my-apps': 'My Apps',
  '/user-management': 'User Management',
  '/change-password': 'Change Password',
  '/dashboard': 'Dashboard',
  '/reports': 'Reports',
  '/customizable-forms': 'Forms',
  '/fill-forms': 'Fill Forms',
  '/customizable-forms-dashboard': 'Forms Dashboard',
};

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname] || '';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userName, setUserName] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    setUserName(localStorage.getItem('userName') || 'User');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsSuperAdmin(user.is_superadmin === true);
  }, []);

  const handleLogout = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
    } catch (err) {
      // still logout even if API fails
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      localStorage.removeItem('userName');
      localStorage.removeItem('username');
      localStorage.removeItem('userRole');
      localStorage.removeItem('portals');
      navigate('/');
    }
  };

  return (
    <SessionGuard>
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        isCollapsed={isCollapsed}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-16 bg-gray-100 border-b border-gray-200 flex items-center px-4 lg:px-6 sticky top-0 z-40 relative">
          {/* Left: mobile menu + collapse toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-800 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <button
              onClick={() => setIsCollapsed(v => !v)}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden lg:flex text-gray-600 hover:text-gray-800 transition-colors p-1"
            >
              {isCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
          </div>

          {/* Center: portal name */}
          <div className="absolute left-1/2 -translate-x-1/2 hidden sm:block text-center">
            <h1 className="text-xl font-bold text-gray-800 tracking-wide">
              ADTW Management Portal
            </h1>
          </div>

          {/* Spacer to push right side to the end */}
          <div className="flex-1"></div>

          {/* Right: user chip + logout */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 border border-gray-200">
              <div className="w-7 h-7 bg-gradient-to-br from-[#3d82c4] to-[#2d6fad] flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-semibold text-gray-800 leading-tight max-w-[140px] truncate">
                  {userName}
                </p>
                <p className="text-[10px] text-[#3d82c4] font-medium leading-tight">
                  {isSuperAdmin ? 'Super Admin' : 'Portal User'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowLogoutConfirm(true)}
              title="Logout"
              className="p-2 text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {pageTitle && (
          <div className="bg-white border-b border-gray-200 px-4 lg:px-6 py-2 flex items-center gap-1.5 text-xs text-gray-500">
            <Home className="w-3.5 h-3.5" />
            <ChevronRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-700 font-medium">{pageTitle}</span>
          </div>
        )}

        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Confirm Logout</h2>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-700">
                  Are you sure you want to logout? You will need to sign in again to access the portal.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowLogoutConfirm(false); handleLogout(); }}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </SessionGuard>
  );
};

export default MainLayout;
